import React from 'react';
import { Link } from 'react-router-dom';
import LocLink from '../components/LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import { getLocalizedImage, getLocalizedAlt } from '../utils/getLocalizedImage';
import {
  Shield,
  Eye,
  Backpack,
  Package,
  GraduationCap,
  Building2,
  Stethoscope,
  Ambulance,
  Briefcase,
  TreePine,
  CheckCircle,
  ChevronRight,
  Users,
  Heart,
} from 'lucide-react';

const HERO_IMAGE = '/images/vbf2u82c_mgp_healthcare-Photoroom.jpg';

const HealthcarePage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  const iconFeatures = [
    {
      icon: Shield,
      en: { title: 'Physical Barrier for Everyday Carry', desc: 'Adds a physical barrier around the original container against some everyday impacts and pressure during transport.' },
      fr: { title: 'Barri\u00e8re physique pour le transport quotidien', desc: 'Ajoute une barri\u00e8re physique autour du contenant original contre certains chocs et pressions du quotidien pendant le transport.' },
    },
    {
      icon: Eye,
      en: { title: 'High-Visibility Identification', desc: 'Clearly marked cases help make the kit easier to identify.' },
      fr: { title: 'Identification tr\u00e8s visible', desc: '\u00c9tuis clairement identifi\u00e9s pour faciliter le rep\u00e9rage de la trousse.' },
    },
    {
      icon: Backpack,
      en: { title: 'Designed for Everyday Transport', desc: 'Designed for daily carry in bags. Follow the manufacturer\u2019s storage instructions and avoid excessive heat or cold.' },
      fr: { title: 'Con\u00e7u pour le transport quotidien', desc: 'Con\u00e7u pour le transport quotidien dans un sac. Respectez les consignes de conservation du fabricant et \u00e9vitez la chaleur ou le froid excessifs.' },
    },
    {
      icon: Package,
      en: { title: 'Bulk & Institutional Orders', desc: 'Scalable supply for schools, clinics, and emergency programs.' },
      fr: { title: 'Commandes institutionnelles', desc: 'Approvisionnement \u00e9volutif pour \u00e9coles, cliniques et programmes d\u2019urgence.' },
    },
  ];

  const whyChoose = [
    {
      icon: Shield,
      en: {
        title: 'A Physical Barrier for Real-World Carry',
        desc: 'Rescue medications are often transported in backpacks, vehicles, or field kits. MyGuardPod cases add a physical barrier around the original container that helps limit some routine impacts, pressure, and everyday wear during transport. Protection remains limited and does not replace the manufacturer\u2019s storage instructions.',
      },
      fr: {
        title: 'Une barri\u00e8re physique pour le transport r\u00e9el',
        desc: 'Les m\u00e9dicaments de secours sont souvent transport\u00e9s dans des sacs \u00e0 dos, des v\u00e9hicules ou des trousses de terrain. Les \u00e9tuis MyGuardPod ajoutent une barri\u00e8re physique autour du contenant d\u2019origine qui aide \u00e0 limiter certains chocs courants, la pression et l\u2019usure quotidienne pendant le transport. La protection demeure limit\u00e9e et ne remplace pas les consignes de conservation du fabricant.',
      },
    },
    {
      icon: Eye,
      en: {
        title: 'Improve On-Sight Identification in an Emergency',
        desc: 'Highly visible cases can help staff or bystanders identify emergency medication more quickly. Identification depends on labeling, positioning, and the emergency plan in place.',
      },
      fr: {
        title: 'Am\u00e9liorer l\u2019identification visuelle en situation d\u2019urgence',
        desc: 'Des \u00e9tuis tr\u00e8s visibles peuvent aider le personnel ou les t\u00e9moins \u00e0 rep\u00e9rer plus rapidement le m\u00e9dicament d\u2019urgence. L\u2019identification d\u00e9pend de l\u2019\u00e9tiquetage, du positionnement et du plan d\u2019urgence en place.',
      },
    },
    {
      icon: Heart,
      en: {
        title: 'Designed with Caregivers and Schools',
        desc: 'Developed through real-world feedback from families, caregivers, and school staff managing emergency medications daily.',
      },
      fr: {
        title: 'Conçu avec les aidants et les écoles',
        desc: 'Développé grâce aux retours concrets de familles, d\u2019aidants et de personnel scolaire gérant des médicaments d\u2019urgence au quotidien.',
      },
    },
  ];

  const useEnvironments = [
    { icon: GraduationCap, en: 'School Health Offices', fr: 'Bureaux de santé scolaires' },
    { icon: Ambulance, en: 'Emergency Response Kits', fr: 'Kits de réponse d\u2019urgence' },
    { icon: Backpack, en: 'Backpack Medication Carry', fr: 'Transport de médicaments en sac à dos' },
    { icon: Stethoscope, en: 'Pharmacy Dispensing Add-Ons', fr: 'Compléments de distribution en pharmacie' },
    { icon: Briefcase, en: 'First Responder Equipment', fr: 'Équipement de premiers répondants' },
    { icon: TreePine, en: 'Travel and Outdoor Activities', fr: 'Voyage et activités extérieures' },
  ];

  const trustedBy = fr
    ? ['Infirmières scolaires', 'Cliniques et pharmacies spécialisées', 'Premiers répondants', 'Familles gérant des médicaments d\u2019urgence']
    : ['School Nurses', 'Clinics & Specialty Pharmacies', 'First Responders', 'Families Managing Emergency Medications'];

  return (
    <main className="pt-20 sm:pt-24">
      <SEOHead
        routeKey="healthcare"
        language={language}
        title={language === 'fr' ? 'Santé & Partenariats — Écoles, cliniques, pharmacies | MyGuardPod' : 'Healthcare & Partnerships — Schools, Clinics, Pharmacies | MyGuardPod'}
        description={language === 'fr'
          ? 'Solutions MyGuardPod pour les écoles, cliniques, pharmacies et services d\u2019urgence : étuis protecteurs, commandes en gros et étiquetage personnalisé.'
          : 'MyGuardPod solutions for schools, clinics, pharmacies and emergency services: protective cases, bulk orders and custom labeling.'}
      />
      <JsonLd data={breadcrumbSchema([{ name: language === 'fr' ? 'Santé' : 'Healthcare', routeKey: 'healthcare' }], language)} />
      {/* Hero with image */}
      <section className="relative overflow-hidden banner-fade">
        <img
          src={getLocalizedImage('healthcareHero', language)}
          alt={getLocalizedAlt('healthcareHero', language)}
          className="w-full h-auto object-cover object-[30%_center] sm:object-center"
        />
      </section>

      {/* Hero CTAs */}
      <section className="bg-[#1a1a1a] pt-14 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex flex-col sm:flex-row gap-4 mb-3">
              <LocLink
                to="contact"
                className="inline-flex items-center justify-center bg-[#E31B23] hover:bg-[#c8171e] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm sm:text-base"
                data-testid="healthcare-hero-bulk-cta"
              >
                {fr ? 'Demander un prix de gros' : 'Request Bulk Pricing'}
              </LocLink>
              <LocLink
                to="contact"
                className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] px-6 py-3 rounded-full font-bold transition-all duration-300 text-sm sm:text-base"
                data-testid="healthcare-hero-contact-cta"
              >
                {fr ? 'Contacter pour commandes institutionnelles' : 'Contact Institutional Orders'}
              </LocLink>
            </div>
            <p className="font-manrope text-xs text-neutral-500">
              {fr ? 'Pour les écoles, cliniques et programmes d\u2019urgence' : 'For schools, clinics, and emergency programs'}
            </p>
          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="bg-[#E31B23] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center">
            <span className="font-outfit font-bold text-white text-sm uppercase tracking-widest">
              {fr ? 'D\u00e9velopp\u00e9 \u00e0 partir de retours d\u2019utilisateurs r\u00e9els' : 'Developed with real-world feedback'}
            </span>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              {trustedBy.map((item, i) => (
                <span key={i} className="font-manrope text-sm text-white/90">
                  {i > 0 && <span className="text-white/50 mr-4">&bull;</span>}
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare limitations & vehicle warning */}
      <section className="py-12 bg-white" data-testid="healthcare-limitations-block">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <p className="font-manrope text-[11px] sm:text-xs text-neutral-500 leading-relaxed">
            {fr
              ? 'Accessoires de transport protecteurs seulement. M\u00e9dicaments et fournitures m\u00e9dicales non inclus. Aucun contr\u00f4le de la temp\u00e9rature.'
              : 'Protective carrying accessories only. Medication and medical supplies are not included. No temperature control.'}
          </p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-6">
            <p className="font-manrope text-sm text-neutral-700 leading-relaxed">
              {fr
                ? 'Les \u00e9tuis ajoutent une barri\u00e8re physique autour des contenants originaux et aident \u00e0 garder les fournitures regroup\u00e9es, organis\u00e9es et faciles \u00e0 identifier. Cette protection demeure limit\u00e9e. Apr\u00e8s une chute ou un impact important, ouvrez l\u2019\u00e9tui et inspectez chaque \u00e9l\u00e9ment conform\u00e9ment aux instructions officielles du fabricant.'
                : 'The cases add a physical barrier around original containers and help keep supplies together, organized, and easier to identify. This protection remains limited. After a significant drop or impact, open the case and inspect every item according to the manufacturer\u2019s official instructions.'}
            </p>
          </div>
          <div className="bg-red-50 border-l-4 border-[#E31B23] rounded-r-2xl p-6">
            <p className="font-manrope text-sm text-neutral-700 leading-relaxed">
              <span className="font-bold text-[#1a1a1a]">{fr ? 'Avertissement v\u00e9hicules\u00a0: ' : 'Vehicle warning: '}</span>
              {fr
                ? 'Ne laissez pas un m\u00e9dicament dans un v\u00e9hicule expos\u00e9 \u00e0 une chaleur ou \u00e0 un froid excessif. Respectez toujours les conditions de conservation indiqu\u00e9es par le fabricant, le pharmacien ou le professionnel de la sant\u00e9.'
                : 'Do not leave medication inside a vehicle exposed to excessive heat or cold. Always follow the storage conditions provided by the manufacturer, pharmacist, or healthcare professional.'}
            </p>
          </div>
        </div>
      </section>

      {/* Icon Features */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {iconFeatures.map((feat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feat.icon className="w-7 h-7 text-[#E31B23]" />
                </div>
                <h3 className="font-outfit font-bold text-[#1a1a1a] mb-2">
                  {fr ? feat.fr.title : feat.en.title}
                </h3>
                <p className="font-manrope text-sm text-neutral-600">
                  {fr ? feat.fr.desc : feat.en.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Healthcare Organizations Choose MyGuardPod */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr
                ? 'Pourquoi les organisations de santé choisissent MyGuardPod'
                : 'Why Healthcare Organizations Choose MyGuardPod'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#E31B23]" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-[#1a1a1a] mb-3">
                  {fr ? item.fr.title : item.en.title}
                </h3>
                <p className="font-manrope text-neutral-600">
                  {fr ? item.fr.desc : item.en.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where MyGuardPod Is Used */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'Où MyGuardPod est utilisé' : 'Where MyGuardPod Is Used'}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useEnvironments.map((env, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-center gap-4 bg-neutral-50 p-5 rounded-2xl"
              >
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <env.icon className="w-6 h-6 text-[#E31B23]" />
                </div>
                <span className="font-manrope font-medium text-[#1a1a1a]">
                  {fr ? env.fr : env.en}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional CTA */}
      <section className="py-20 sm:py-24 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-white mb-4">
              {fr
                ? 'Commandes en gros pour les organisations de santé'
                : 'Bulk Orders for Healthcare Organizations'}
            </h2>
            <p className="font-manrope text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
              {fr
                ? 'MyGuardPod soutient les écoles, cliniques, pharmacies et programmes d\u2019urgence avec des commandes en gros et des options d\u2019étiquetage personnalisé.'
                : 'MyGuardPod supports schools, clinics, pharmacies, and emergency programs with bulk ordering and custom labeling options.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LocLink
                to="contact"
                className="inline-flex items-center justify-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
                data-testid="healthcare-bulk-cta"
              >
                <span>{fr ? 'Demander un prix de gros' : 'Request Bulk Pricing'}</span>
              </LocLink>
              <LocLink
                to="contact"
                className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                data-testid="healthcare-contact-cta"
              >
                <span>{fr ? 'Contacter pour commandes institutionnelles' : 'Contact for Institutional Orders'}</span>
                <ChevronRight className="w-5 h-5" />
              </LocLink>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HealthcarePage;
